import { graphql, SliceComponentProps } from 'gatsby';
import React, { useEffect, useState } from 'react';

import { ReactComponent as FooterBgLeft } from '/src/images/icons/footerbg-left.svg';
import { ReactComponent as FooterBgRight } from '/src/images/icons/footerbg-right.svg';
import { ReactComponent as SpeechBubble } from '/src/images/icons/speechbubble.svg';

import { isBrowser } from '../../helpers/is-browser';
import { footerIsMeetingPlace } from '../../helpers/list-page/type-guards';
import { FooterSliceContext } from '../../helpers/rendered-page-creation/create-slices';
import {
  clearUrlParams,
  getAppStoreLink,
  getLocalStorageItem,
  getPlayStoreLink,
  setLocalStorageItem,
} from '../../helpers/utils';
import { useGetLibraryDictionary } from '../../hooks/use-get-library-dictionary';
import { ISliceComponentFooterFragment } from '../../queries/fragments/sliceComponentFooter';
import { IStoreImage } from '../../queries/fragments/storeImage';
import ImageWrapper from '../image-wrapper/Image-wrapper';
import NewLetterForm from '../newsletter/newsletter-form';
import { OpeningHours } from '../opening-hours/opening-hours';
import { SocialMediaLinksList } from '../social-media-links-list/social-media-links-list';
import LinkWrapper from '../ui/link-wrapper/link-wrapper';
import { Modal } from '../ui/modal/modal';
import { ModalBody } from '../ui/modal/modal-body';
import { SideModal } from '../ui/side-modal/side-modal';
import { SideModalBody } from '../ui/side-modal/side-modal-body';
export interface FooterProps {
  contentfulMeetingPlacePageHome: ISliceComponentFooterFragment;
  appStoreImage: IStoreImage;
  playStoreImage: IStoreImage;
}

export const Footer = ({
  data,
  sliceContext,
  footerClass,
}: SliceComponentProps<FooterProps, FooterSliceContext> & {
  footerClass?: string;
}) => {
  const [feedbackModalOpen, setFeedbackModallOpen] = useState<boolean>(false);
  const [sliderOpen, setSliderOpen] = useState<boolean>(false);
  const dictionary = useGetLibraryDictionary(sliceContext.node_locale);

  const meetingPlace = data?.contentfulMeetingPlacePageHome?.meetingPlace;
  const footer = data?.contentfulMeetingPlacePageHome?.footer;

  const isMeetingPlaceFooter = footerIsMeetingPlace(footer);

  const showAppDownloadLinks =
    process.env.GATSBY_IOS_APP_ID || process.env.GATSBY_ANDROID_APP_ID;
  const appStoreImage = data?.appStoreImage;
  const playStoreImage = data?.playStoreImage;

  const isPlayStoreSvgIcon = !!playStoreImage?.svg?.content;
  const isAppStoreSvgIcon = !!appStoreImage?.svg?.content;

  const copyRightText =
    '© Ingka Centres Holding B.V. ' + new Date().getFullYear();

  const aspectRatio =
    meetingPlace?.logo?.gatsbyImageData?.width &&
    meetingPlace?.logo?.gatsbyImageData?.height
      ? meetingPlace?.logo.gatsbyImageData.width /
        meetingPlace?.logo.gatsbyImageData.height
      : undefined;

  useEffect(() => {
    isBrowser &&
      typeof window.OneTrust !== 'undefined' &&
      window.OneTrust.initializeCookiePolicyHtml(true);
  }, []);

  if (!isMeetingPlaceFooter) return;

  const sendEventToDataLayer = (interaction_type: string) => {
    if (isBrowser && dataLayer) {
      dataLayer.push({
        event: 'newsletter_form_subscription',
        user_subscribed: interaction_type,
      });
    }
  };

  return (
    <>
      <footer
        className={`relative mt-auto pb-28 text-neutral-grey-700 md:pb-5 ${footerClass}`}
        data-elastic-exclude
      >
        <div className="relative mx-6 mt-20 xl:container lg:mt-24 lg:px-36 xl:px-28">
          <div className="grid xl:grid-flow-col">
            <div className="md:row-span-2 md:pr-28">
              {meetingPlace?.logo && (
                <ImageWrapper
                  alt="Logotype"
                  file={meetingPlace?.logo?.file}
                  gatsbyImageData={meetingPlace?.logo?.gatsbyImageData}
                  svg={meetingPlace?.logo?.svg}
                  className={
                    aspectRatio == 1
                      ? 'mb-10 max-h-15 w-36'
                      : 'mb-10 max-h-10 w-36'
                  }
                  imgClassName={
                    aspectRatio == 1 ? 'max-h-15 !w-auto' : 'max-h-10 !w-auto'
                  }
                />
              )}
            </div>
            <div className="text-bodym sm:col-span-3">
              <div className="pb-8 sm:pb-20">
                <div className="grid gap-8 sm:grid-cols-3 sm:gap-3 md:gap-8">
                  <div>
                    <h2 className="mb-2 font-title text-headings font-bold">
                      {meetingPlace?.name}
                    </h2>
                    {meetingPlace?.visitorsAddressStreet &&
                      meetingPlace?.visitorsAddressStreet}

                    {meetingPlace?.visitorsAddressStreet2 && <br />}
                    {meetingPlace?.visitorsAddressStreet2}

                    {(meetingPlace?.visitorsAddressStreet ||
                      meetingPlace?.visitorsAddressStreet2) && <br />}

                    {meetingPlace?.visitorsAddressZipCode &&
                      `${meetingPlace?.visitorsAddressZipCode} `}

                    {meetingPlace?.visitorsAddressCity}

                    {(meetingPlace?.visitorsAddressZipCode ||
                      meetingPlace?.visitorsAddressCity) && <br />}
                    {meetingPlace?.phoneNumber && (
                      <a
                        className="underline hover:text-text-light"
                        href={`tel:${meetingPlace?.phoneNumber?.replaceAll(
                          /\D+/g,
                          '',
                        )}`}
                      >
                        {meetingPlace?.phoneNumber}
                      </a>
                    )}
                    {showAppDownloadLinks && (
                      <div className="mt-4 flex">
                        {playStoreImage && (
                          <a href={getPlayStoreLink()}>
                            <ImageWrapper
                              gatsbyImageData={playStoreImage?.gatsbyImageData}
                              file={playStoreImage?.file}
                              svg={playStoreImage?.svg}
                              alt={
                                playStoreImage?.description
                                  ? playStoreImage?.description
                                  : ''
                              }
                              className={`h-12 ${
                                isPlayStoreSvgIcon ? 'mx-1 mt-3 w-28' : 'w-32'
                              }`}
                            />
                          </a>
                        )}
                        {appStoreImage && (
                          <a href={getAppStoreLink()}>
                            <ImageWrapper
                              gatsbyImageData={appStoreImage?.gatsbyImageData}
                              file={appStoreImage?.file}
                              svg={appStoreImage?.svg}
                              alt={
                                appStoreImage?.description
                                  ? appStoreImage?.description
                                  : ''
                              }
                              className={`h-12 ${
                                isAppStoreSvgIcon ? 'mx-1 mt-3 w-28' : 'w-32'
                              }`}
                            />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="mb-3 font-title text-headings font-bold">
                      <OpeningHours
                        variant="today"
                        openingHoursClosed={
                          dictionary?.openingHoursClosed ?? ''
                        }
                        openingHoursClosedToday={
                          dictionary?.openingHoursClosedToday ?? ''
                        }
                        openingHoursOpenToday={
                          dictionary?.openingHoursOpenToday ?? ''
                        }
                        openingHours={meetingPlace?.openingHours}
                        openingHoursExceptions={
                          meetingPlace?.openingHoursExceptions
                        }
                        timezone={meetingPlace?.timezone}
                        node_locale={sliceContext.node_locale}
                      />
                    </div>
                    <div className="text-text-light">
                      <OpeningHours
                        openingHoursClosed={
                          dictionary?.openingHoursClosed ?? ''
                        }
                        openingHoursClosedToday={
                          dictionary?.openingHoursClosedToday ?? ''
                        }
                        openingHoursOpenToday={
                          dictionary?.openingHoursOpenToday ?? ''
                        }
                        openingHours={meetingPlace?.openingHours}
                        openingHoursExceptions={
                          meetingPlace?.openingHoursExceptions
                        }
                        timezone={meetingPlace?.timezone}
                        node_locale={sliceContext.node_locale}
                        variant="compact"
                      />
                    </div>
                    {footer?.allOpeningHoursLinkText &&
                      footer?.allOpeningHoursPageLink?.slug && (
                        <LinkWrapper
                          data={footer?.allOpeningHoursPageLink}
                          className="mt-2 inline-block underline hover:text-text-light"
                        >
                          {footer.allOpeningHoursLinkText}
                        </LinkWrapper>
                      )}
                  </div>
                  <div>
                    <div className="relative mx-auto w-52 text-center lg:mx-0">
                      <SpeechBubble className="h-36 w-52" />
                      <div className="absolute left-2 top-0 flex h-[90%] w-full max-w-[90%] flex-col items-center justify-center p-4 text-brand-green-3">
                        {footer?.feedbackText && (
                          <h2 className="font-title text-headings">
                            {footer.feedbackText}
                          </h2>
                        )}
                        <div>
                          {footer?.feedbackLinkText && (
                            <button
                              className="text-bodys underline hover:text-brand-green-2"
                              tabIndex={0}
                              onClick={() => setFeedbackModallOpen(true)}
                            >
                              {footer.feedbackLinkText}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="pb-8 sm:pb-20">
                <div className="grid gap-0 text-bodys sm:grid-cols-3 sm:gap-8">
                  {footer?.links?.map((menuGroup, index) => (
                    <ul
                      className="pt-6 first:pt-0 sm:pt-0"
                      key={`menu-group-${index}`}
                    >
                      {menuGroup.pages?.map((page, index) => (
                        <li key={`${page.name}-${index}`}>
                          {page.__typename === 'ContentfulLibraryPopupModal' ? (
                            <button
                              id="newsletter-button"
                              className="mb-3 text-bodys hover:text-text-light hover:underline"
                              onClick={() => {
                                window.scrollTo(0, 0);
                                setSliderOpen(true);
                              }}
                            >
                              {page.name}
                            </button>
                          ) : (
                            <LinkWrapper
                              data={page}
                              className="mb-3 block hover:text-text-light hover:underline"
                            >
                              {page.__typename ===
                              'ContentfulLibraryExternalLink'
                                ? page.linkTitle
                                : page.name}
                            </LinkWrapper>
                          )}
                        </li>
                      ))}
                      {index === footer.links.length - 1 && (
                        <button className="ot-sdk-show-settings mb-3 text-bodys hover:text-text-light hover:underline">
                          {dictionary?.cookieSettings}
                        </button>
                      )}
                    </ul>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex flex-col text-bodys lg:flex-row">
                  <div className="order-2 sm:order-1 lg:basis-1/3">
                    {copyRightText}
                  </div>
                  <div className="order-1 mb-10 mt-3 sm:my-0 lg:basis-2/3">
                    {meetingPlace?.socialMediaLinks && (
                      <SocialMediaLinksList
                        socialMediaLinks={meetingPlace.socialMediaLinks}
                        itemCssClass="mx-1.5 lg:mx-2.5 lg:ml-0 lg:mr-5"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="pointer-events-none absolute bottom-0 left-0 -z-50 h-52 w-full sm:h-[31rem]">
          <FooterBgLeft className="absolute bottom-0 left-0 w-44 fill-accent-primary-normal lg:w-[26rem]" />
          <FooterBgRight className="absolute bottom-0 right-0 w-52 fill-accent-primary-normal lg:w-[31rem]" />
        </div>
      </footer>
      <Modal
        isOpen={feedbackModalOpen}
        handleClose={() => setFeedbackModallOpen(!feedbackModalOpen)}
      >
        <ModalBody>
          <iframe
            className="h-full w-full border-none lg:p-5"
            src={footer?.feedbackUrl}
          ></iframe>
        </ModalBody>
      </Modal>
      <SideModal
        isOpen={sliderOpen}
        handleClose={() => {
          if (isBrowser) {
            // Save to localStorage when the user closes the popup without subscribing
            setLocalStorageItem('hasClosedWithoutSubscribing', 'true');
            const hasSubscribedToNewsletter = getLocalStorageItem(
              'hasSubscribedToNewsletter',
            );
            if (!hasSubscribedToNewsletter) {
              sendEventToDataLayer('No');
            }
            setLocalStorageItem(
              'lastPopupClosedDate',
              new Date().toISOString(),
            );
          }
          setSliderOpen(!sliderOpen);
          clearUrlParams();
        }}
      >
        <SideModalBody>
          <NewLetterForm
            locale={sliceContext.node_locale}
            integrationId={
              data.contentfulMeetingPlacePageHome.meetingPlace.integrationId
            }
            businessUnitCode={
              data.contentfulMeetingPlacePageHome.meetingPlace.businessUnitCode
            }
            handleClose={() => {
              setSliderOpen(!sliderOpen);
              clearUrlParams();
            }}
          />
        </SideModalBody>
      </SideModal>
    </>
  );
};

export default Footer;

export const query = graphql`
  query footerSliceQuery(
    $tagId: String!
    $node_locale: String!
    $appStoreImageId: String
    $playStoreImageId: String
  ) {
    contentfulMeetingPlacePageHome(
      metadata: { tags: { elemMatch: { contentful_id: { eq: $tagId } } } }
      node_locale: { eq: $node_locale }
    ) {
      ...SliceComponentFooterFragment
    }
    appStoreImage: contentfulAsset(
      contentful_id: { eq: $appStoreImageId }
      node_locale: { eq: $node_locale }
    ) {
      gatsbyImageData(width: 140, height: 60, formats: AVIF)
      description
      ... on ContentfulAsset {
        ...Svg
      }
    }
    playStoreImage: contentfulAsset(
      contentful_id: { eq: $playStoreImageId }
      node_locale: { eq: $node_locale }
    ) {
      gatsbyImageData(width: 140, height: 60, formats: AVIF)
      description
      ... on ContentfulAsset {
        ...Svg
      }
    }
  }
`;
